import { Formiz, useForm } from "@formiz/core";
import { isMaxLength, isMinLength, isRequired } from "@formiz/validations";
import {
  CHARACTER_MODERATION_STATUS,
  CHARACTER_VISIBILITY,
  FeaturePermissions,
  isCharacterTokenCountValid,
  NotificationType,
  useKindeFeatureFlags,
  useLorebook,
  useTranslatedVoiceSampleScript,
  useTranslation,
  useUserInfo,
  useUserVoices,
  useVoice,
} from "@next-platform/client-common-core";
import {
  CapabilityGate,
  SubCapabilityKey,
  useApplicationCapabilitiesAllowed,
  useApplicationCapabilityReady,
} from "@next-platform/client-common-core/infra/application/capabilities";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import useRulebookAttachmentSelection from "@next-platform/client-common-core/rulebook/attach/useRulebookAttachmentSelection";
import { MAX_TOKEN_LIMIT } from "@next-platform/client-common-core/utils/isCharacterTokenCountValid";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  Icon,
  IconSize,
  IconVariants,
  Input,
  Size,
  Switch,
  Text,
  TextArea,
  TextVariants,
  TokenCounter,
} from "@next-platform/client-common-spicy-ui";
import { Gender, Lorebook, VoiceStatus, VoiceWithCustomVoicePublicFields } from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import useAvatarUpload from "@/components/CharacterForm/useAvatarUpload";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import DiscardChangesModalV2 from "@/components/common/DiscardChangesModalV2";
import ReactRouterPrompt from "@/components/common/ReactRouterPrompt";
import { FormField } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import {
  CHARACTER_FORM_VALIDATION_SCHEMA,
  EXTERNAL_ROUTES_FOR_APPLICATION,
  FEATURE_FLAGS,
  getDraftStorageKey,
  MAXIMUM_TAG_LIMIT,
  MINIMUM_TAG_LIMIT,
  MY_CHATBOT_PAGE_ROUTE,
  ROUTES,
  THIRD_PARTY_APP_IDS,
} from "@/constants";
import { CUSTOM_ERROR_MESSAGES } from "@/constants/ErrorMessages";
import getUserPlan from "@/features/user/utils/getUserPlan";
import { useFeatureFlag } from "@/hooks";
import { useFieldsSuggestion } from "@/hooks/useFieldsSuggestion";
import useToast from "@/hooks/useToast";
import useTokenCounter from "@/hooks/useTokenCounter";
import characterFormLogger from "@/infra/characterFormLogger";
import { IChatVoice, VoiceCategories } from "@/pages/Chat/components/voices/types";
import { ChatModalVoiceSelection } from "@/pages/Chat/components/VoiceSelectionModal";
import { transformVoices } from "@/pages/Chat/utils/transformVoices";
import { CHATBOT_SAVE_DEFERRED_FOR_MODERATION_CONFIRM } from "@/pages/Chatbot/constants";
import { chatBotrequiredFields, PLACEHOLDER_STYLING, SUCCESS_MESSAGE } from "@/pages/CreateCharacter/v2/constants";
import useVoicePreview from "@/pages/VoiceClonePreview/useVoicePreview";
import { FormTypes, type IDraftChracter } from "@/types";
import type { ImportedCharacter } from "@/types/imported-character";
import {
  cn,
  findFirstInvalidFormizField,
  getIsPartner,
  isStringContainingURL,
  replaceMessageTokens,
  scrollToFirstInvalidField,
  stripLanguagePrefix,
} from "@/utils";
import { handleCheckIfTagsAreEqual } from "@/utils/handleCheckIfTagsAreEqual";
import { buildLorebookImportRequest, isLorebookImportEnvelope } from "@/utils/importHelpers";

import { CharacterFormFields, CharacterFormProps, CustomErrorMessage, DeepTag, SuggestedFieldButtonProps } from "../types";
import AvatarCreate from "./AvatarCreate";
import CharacterFormAdvanced from "./CharacterFormAdvanced";
import CharacterTags from "./CharacterTags";
import GenerateSuggestedFieldButton from "./GenerateSuggestedFieldButton";
import SubmitForReviewModal from "./SubmitForReviewModal";
import UnlockCustomVoicesModal from "./UnlockCustomVoicesModal";
import getCharacterFormPlaceholders from "./utils/getCharacterFormPlaceholders";
import VisibilityButtons from "./VisibilityButtons";

function CharacterForm({
  submitForManualReview,
  id = "",
  // TODO(karlbernard2): [NX-13090] prop mirrors the API's snake_case character_id; drop the suppression when props are camelCased.
  // eslint-disable-next-line @typescript-eslint/naming-convention -- the canonical id comes from the API's character_id field
  character_id,
  name = "",
  title = "",
  persona = "",
  greeting = "",
  scenario = "",
  avatar_url = "",
  visibility = CHARACTER_VISIBILITY.PRIVATE,
  dialogue = "",
  onSave,
  allowImport = false,
  setIsSubmitForManualReview,
  isSubmitting,
  submitError,
  guidelines = false,
  tags = [],
  view,
  definition_visible = false,
  groupAddable = true,
  isEdit,
  moderationReport,
  draftId,
  isAlreadyDraft = false,
  errorCode,
  lorebooks,
  rulebook_id: draftRulebookId,
  voice_id,
  handleModerationSubmitConfirmClose,
  stateShowModerationSubmitConfirm,
  stateModerationConfirmLoading,
  handleModerationSubmitConfirm,
}: CharacterFormProps) {
  const { displayToast } = useToast();
  const initialValues = {
    name,
    avatar_url,
    title,
    persona,
    greeting,
    scenario: scenario?.length > 1 ? scenario : "",
    dialogue: dialogue?.length > 1 ? dialogue : "",
    voice_id,
    visibility,
    guidelines,
    tags,
    definition_visible,
    group_addable: visibility === CHARACTER_VISIBILITY.PUBLIC ? (groupAddable ?? true) : false,
  };
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const formPlaceholders = getCharacterFormPlaceholders(t);
  const { getTranslatedVoiceSampleScript } = useTranslatedVoiceSampleScript();
  function setDialogMessage() {
    if (submitForManualReview) return t("chatbot:modal.review.manualReviewMessage");
    const pathSegment = pathname.split("/")[2];
    let confirmationMessage = t("chatbot:modal.review.confirmationMessage.default");
    if (pathSegment === "edit") {
      confirmationMessage = t("chatbot:modal.review.confirmationMessage.edit");
    } else if (pathSegment === "create") {
      confirmationMessage = t("chatbot:modal.review.confirmationMessage.create");
    }
    return confirmationMessage;
  }

  const confirmText = setDialogMessage();
  const navigate = useNavigate();
  const { onCharacterCardUpload, importedJsonData, avatarFileKey, clearImportedJsonData } = useAvatarUpload();
  const characterForm = useForm();
  const { isValid, setFieldsValues, isStepPristine, values, reset } = characterForm;
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<IDraftChracter | null>(null);
  const [showRequiredFormFieldsError, setShowRequiredFormFieldsError] = useState(false);
  const [tokenWarningMessage, setTokenWarningMessage] = useState("");
  const [tokenError, setTokenError] = useState(false);
  const [tokenErrorMessage, setTokenErrorMessage] = useState("");
  // Hoisted above `submitForm` so its guard can reference `isTokenCounterReady`.
  const { countTokens, isReady: isTokenCounterReady } = useTokenCounter();
  const [successMessage, setSuccessMessage] = useState("");
  const [error] = useState("");
  const [showPrompt, setShowPrompt] = useState(false);
  const skipUnsavedChangesPromptRef = useRef(false);
  const [, setPersonaValue] = useState("");
  const [botVisibility, setBotVisibility] = useState(initialValues.visibility ?? CHARACTER_VISIBILITY.PRIVATE);
  const [stateShowFavoriteWarningModal, setStateShowFavoriteWarningModal] = useState(false);
  const [stateSelectedVoiceId, setStateSelectedVoiceId] = useState<string | undefined>(voice_id);
  const [stateShowVoiceSelectionModal, setStateShowVoiceSelectionModal] = useState(false);
  const [stateShowVoiceUpgradeModal, setStateShowVoiceUpgradeModal] = useState(false);
  const [stateSelectedVoiceCategory, setStateSelectedVoiceCategory] = useState<VoiceCategories>(VoiceCategories.ALL);
  const { userId, username, permissions } = useUserInfo();
  const { hasAccessToTTS } = useKindeFeatureFlags();
  const { isUserPremium } = getUserPlan();
  const { hasFeatureFlag: hasBotUpdateModeration } = useFeatureFlag(FEATURE_FLAGS.PUBLIC_BOT_EDIT_MODERATION);
  const { hasFeatureFlag: hasCustomVoiceFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.TTS);
  const { hasFeatureFlag: hasVoiceAttachmentFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.CHARACTER_VOICE_ATTACHMENT);
  const applicationId = basicAppConfig.applicationId;
  const { onUserImportLorebook } = useLorebook();
  const { getVoices, allVoices, loading: voiceLoading } = useVoice();
  const { userVoices, getUserVoices, isLoading: isUserVoicesLoading } = useUserVoices();
  const initialTagsValue = initialValues.tags;
  const characterTagsValue = values.tags;
  const { handleGenerateSuggestField, activeGeneratingField } = useFieldsSuggestion({ form: characterForm });
  const isGreetingUrlError = isStringContainingURL(values.greeting);
  const isTitleUrlError = isStringContainingURL(values.title);
  const isNameUrlError = isStringContainingURL(values.name);
  const isPersonaUrlError = isStringContainingURL(values.persona);
  const isTitleAndNameFilled = values.title?.trim()?.length >= 3 && values.name?.trim()?.length >= 3;
  const greetingUrlErrorMessage = t("chatbot:form.validation.greeting.nouRLS", { url: isGreetingUrlError?.join(", ") });
  const titleUrlErrorMessage = t("chatbot:form.validation.title.noUrls", { url: isTitleUrlError?.join(", ") });
  const nameUrlErrorMessage = t("chatbot:form.validation.name.noUrls", { url: isNameUrlError?.join(", ") });
  const personaUrlErrorMessage = t("chatbot:form.validation.persona.noUrls", { url: isPersonaUrlError?.join(", ") });
  const isBotVisibilityPublic = botVisibility === CHARACTER_VISIBILITY.PUBLIC;
  const isSelectedTagsValid = (isBotVisibilityPublic && characterTagsValue?.length >= MINIMUM_TAG_LIMIT) || !isBotVisibilityPublic;
  const moderationStatus = moderationReport?.moderation_status;
  const waitingForApproval =
    moderationStatus === CHARACTER_MODERATION_STATUS.PENDING || moderationStatus === CHARACTER_MODERATION_STATUS.REREVIEW_REQUIRED;
  const isFixRequired = moderationStatus === CHARACTER_MODERATION_STATUS.FIX_REQUIRED;
  const isPermanentlyPrivate = visibility === CHARACTER_VISIBILITY.PERMANENTLY_PRIVATE;
  const prevValuesRef = useRef({});
  // Track which avatarFileKey from import has been processed to prevent overwriting user's manual avatar changes
  const processedAvatarFileKeyRef = useRef<string | null>(null);
  const isTagsFieldRequiredAndValid = values.tags?.length >= MINIMUM_TAG_LIMIT && isBotVisibilityPublic; // exception to avoid flicker effect where create button gets enabled for few seconds because tags rules take some time to give effect. Because they depends on external factors.

  const isCharacterUploadAvatarAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.CharacterUploadAvatar);
  const isCharacterImportCardAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.CharacterImportCard);
  // Gated inside the hook (rulebook.characterAttach + rulebook.manage): gated off, it fetches nothing and saves nothing.
  // Only a saved character has an attachment to look up; a restored create draft's `id` is a local draft id.
  // The lookup must use the canonical id saves write under (`character_id` when present), like ChatbotEdit does.
  const rulebookSelection = useRulebookAttachmentSelection({
    ownerType: "character",
    ownerId: isEdit ? (character_id ?? id) : "",
    draftRulebookId,
  });
  const isReady = useApplicationCapabilityReady();
  const isAvatarUploadAllowed = isReady && isCharacterUploadAvatarAllowed;
  const requiredFields = isAvatarUploadAllowed
    ? chatBotrequiredFields
    : chatBotrequiredFields.filter((field) => field !== CharacterFormFields.AVATAR);
  const isAnyRequiredFieldValid = requiredFields.some((field) => characterForm.fields[field]?.isValid) || isTagsFieldRequiredAndValid;

  const importingCharactersDocURL = `${EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.docs}/advanced/importing-characters`;
  const [currentLorebooks, setCurrentLorebooks] = useState<Lorebook[]>([]);
  const [initialLorebooks, setInitialLorebooks] = useState<Lorebook[]>([]);
  const [shouldExpandAdvanced, setShouldExpandAdvanced] = useState(false);
  const hasInitializedLorebooks = useRef(false);
  const persistedDraftIdRef = useRef<string | null>(null);
  // Saving a draft while editing keeps the form mounted, so the parent never recomputes
  // `draftId`/`isAlreadyDraft`. Track it here or the submit guards below stay stuck on their
  // mount-time values and reject the resubmit.
  const [hasPersistedDraft, setHasPersistedDraft] = useState(false);
  const refVoicePreviewAudio = useRef<HTMLAudioElement | null>(null);
  const refCustomPreviewReset = useRef<(() => void) | null>(null);
  const {
    previewVoice,
    stopPreview: stopCustomVoicePreview,
    isGenerating: isCustomVoiceGenerating,
    isPlaying: isCustomVoicePlaying,
  } = useVoicePreview();

  // Initialize currentLorebooks and initialLorebooks from lorebooks prop
  useEffect(() => {
    if (lorebooks === undefined) {
      // The form is not remounted between create/:draftId and create, so clear
      // lorebook state when landing on a fresh create; edit/draft keep theirs
      // while their data is still loading
      if (!isEdit && !draftId) {
        setCurrentLorebooks([]);
        setInitialLorebooks([]);
        hasInitializedLorebooks.current = false;
      }
      return;
    }

    setCurrentLorebooks(lorebooks || []);

    // Set initialLorebooks only once so restored drafts don't count as changes
    if (!hasInitializedLorebooks.current) {
      setInitialLorebooks(lorebooks || []);
      hasInitializedLorebooks.current = true;
    }
  }, [lorebooks, isEdit, draftId]);

  useEffect(() => {
    setFieldsValues(initialValues);
  }, [JSON.stringify(Object.values(initialValues))]);

  const valueIfchange = handleCheckIfTagsAreEqual(characterTagsValue, initialTagsValue);

  const keysToExcludeOnEdit = [CharacterFormFields.GUIDELINES];
  const keysToExcludeOnCreate = [
    CharacterFormFields.DEFINITION_VISIBLE,
    CharacterFormFields.VISIBILITY,
    CharacterFormFields.SCENARIO,
    CharacterFormFields.DIALOGUE,
    CharacterFormFields.GUIDELINES,
    CharacterFormFields.GROUP_ADDABLE,
  ];

  const filteredKeysForEdit = Object.keys(initialValues).filter((key) => !keysToExcludeOnEdit.includes(key as CharacterFormFields));

  // Check if Lorebook has changed
  const hasLorebookChanged = useMemo(() => {
    const initialLorebookIds = (initialLorebooks || []).map((lb) => lb.id).sort();
    const currentLorebookIds = (currentLorebooks || []).map((lb) => lb.id).sort();

    return JSON.stringify(initialLorebookIds) !== JSON.stringify(currentLorebookIds);
  }, [initialLorebooks, currentLorebooks]);

  const hasVoiceChanged = stateSelectedVoiceId !== initialValues.voice_id;

  const isDetailsChangedOnEdit =
    filteredKeysForEdit.some((key) => {
      // Create a type so we can satisfy indexing
      type InitialValue = keyof typeof initialValues;

      // voice_id is tracked separately via stateSelectedVoiceId; skip it here
      if (key === "voice_id") return false;

      // Check if initialValues[key] and values[key] are arrays
      const isInitialValueIsArray = Array.isArray(initialValues[key as InitialValue]);
      const isCharacterValueIsArray = Array.isArray(values[key]);

      // If either initialValues[key] or values[key] is an array
      if (isInitialValueIsArray || isCharacterValueIsArray) {
        return !valueIfchange;
      }

      // Compare initialValues[key] and values[key] if they are not arrays
      return initialValues[key as InitialValue] !== values[key];
    }) ||
    hasLorebookChanged ||
    rulebookSelection.hasChanged ||
    hasVoiceChanged;

  const filteredKeysForCreate = Object.keys(values).filter((key) => !keysToExcludeOnCreate.includes(key as CharacterFormFields));

  const isDetailsChangedOnCreate = filteredKeysForCreate.some((key) => {
    const value = values[key]; // Get the value associated with the key

    // Check if the value is an array and it's not empty
    if (Array.isArray(value) && value.length > 0) {
      return true;
    }
    //if tags are is empty then match other values
    if (!Array.isArray(value) && value !== null && value !== "") {
      return true;
    }

    return false;
  });

  const hasDraft = Boolean(draftId) || isAlreadyDraft || hasPersistedDraft;

  const isSaveButtonVisuallyDisabled =
    waitingForApproval || !isAnyRequiredFieldValid || !isTokenCounterReady || isSubmitting || (isEdit && !isDetailsChangedOnEdit && !hasDraft);

  useEffect(() => {
    setShowPrompt((isDetailsChangedOnEdit && isDetailsChangedOnCreate) || hasLorebookChanged || rulebookSelection.hasChanged);
  }, [isDetailsChangedOnCreate, isDetailsChangedOnEdit, hasLorebookChanged, rulebookSelection.hasChanged]);

  useEffect(() => {
    if (showPrompt) {
      skipUnsavedChangesPromptRef.current = false;
    }
  }, [showPrompt]);

  const shouldBlockNavigation = useCallback(
    ({ currentLocation, nextLocation }: { currentLocation: { pathname: string }; nextLocation: { pathname: string } }) => {
      if (skipUnsavedChangesPromptRef.current) return false;
      if (isSubmitting) return false;
      if (!showPrompt) return false;
      const currentPath = stripLanguagePrefix(currentLocation.pathname);
      const nextPath = stripLanguagePrefix(nextLocation.pathname);
      if (currentPath === nextPath) return false;
      return true;
    },
    [showPrompt, isSubmitting],
  );

  //when we have an import
  useEffect(() => {
    if (!importedJsonData) {
      return;
    }

    let parsedImport: unknown = importedJsonData;

    if (typeof importedJsonData === "string") {
      try {
        parsedImport = JSON.parse(importedJsonData);
      } catch (error) {
        characterFormLogger.warn("Failed to parse imported character JSON", error);
        return;
      }
    }

    if (!parsedImport || typeof parsedImport !== "object") {
      return;
    }
    reset();
    const envelope = isLorebookImportEnvelope(parsedImport) ? parsedImport : undefined;
    const characterData = (envelope ? envelope.data : parsedImport) as Record<string, unknown>;
    const imported = characterData as ImportedCharacter;

    if (typeof (imported?.name || imported?.char_name) === "string") {
      setFieldsValues({ name: (imported.name || imported.char_name || "").trim() });
    }
    if (typeof (imported?.title || imported?.personality) === "string") {
      setFieldsValues({
        title: (imported.title || imported.personality || "").substring(0, 100),
      });
    }
    if (typeof (imported?.char_greeting || imported?.first_mes) === "string") {
      setFieldsValues({ greeting: (imported.char_greeting || imported.first_mes || "").trim() });
    }
    if (typeof (imported?.description || imported?.char_persona) === "string") {
      setFieldsValues({ persona: (imported.description || imported.char_persona || "").trim() });
    }
    if (typeof (imported?.example_dialogue || imported?.mes_example) === "string") {
      setFieldsValues({ dialogue: (imported.example_dialogue || imported.mes_example || "").trim() });
    }
    if (typeof (imported?.scenario || imported?.world_scenario) === "string") {
      setFieldsValues({ scenario: (imported.scenario || imported.world_scenario || "").trim() });
    }

    void (async () => {
      const { request: importRequest } = buildLorebookImportRequest(parsedImport);

      try {
        const { data: importedLorebook, error } = await onUserImportLorebook(importRequest);

        if (importedLorebook && !error) {
          setCurrentLorebooks((prev: Lorebook[]) => {
            if (prev.some((lorebook) => lorebook.id === importedLorebook.id)) {
              return prev;
            }
            return [...prev, importedLorebook];
          });
          setShouldExpandAdvanced(true);
          displayToast({ message: "Lorebook imported", type: NotificationType.success });
        } else if (error) {
          displayToast({ message: error, type: NotificationType.error });
        }
      } catch (error) {
        characterFormLogger.error("Failed to import lorebook", error);
        displayToast({ message: "Failed to import lorebook.", type: NotificationType.error });
      }
    })();
    clearImportedJsonData();
  }, [importedJsonData, setFieldsValues, onUserImportLorebook, displayToast, setCurrentLorebooks, setShouldExpandAdvanced, reset]);

  // Set avatar_url from imported avatarFileKey only once, and only if user hasn't manually changed it
  // Why: Prevents the imported avatar from overwriting user's manual changes (generate/upload)
  useEffect(() => {
    if (avatarFileKey && avatarFileKey !== processedAvatarFileKeyRef.current) {
      const currentAvatarUrl = values.avatar_url;
      // Only update if avatar_url is empty, unchanged from initial, or was set by a previous import (user hasn't manually modified it)
      if (!currentAvatarUrl || currentAvatarUrl === initialValues.avatar_url || currentAvatarUrl === processedAvatarFileKeyRef.current) {
        characterForm.setFieldsValues({ avatar_url: avatarFileKey });
        processedAvatarFileKeyRef.current = avatarFileKey;
      }
    }
  }, [avatarFileKey, characterForm, values.avatar_url, initialValues.avatar_url]);

  const hasCustomVoicesAccess = permissions?.includes(FeaturePermissions.TTS_CUSTOM_VOICES) ?? false;
  const canUseCustomVoices = hasCustomVoiceFeatureFlag && hasCustomVoicesAccess;

  const stopVoicePreview = useCallback(() => {
    if (!refVoicePreviewAudio.current) {
      stopCustomVoicePreview();
      return;
    }

    refVoicePreviewAudio.current.pause();
    refVoicePreviewAudio.current.currentTime = 0;
    refVoicePreviewAudio.current.src = "";
    refVoicePreviewAudio.current = null;
    stopCustomVoicePreview();
  }, [stopCustomVoicePreview]);

  const stopAllVoicePreview = useCallback(() => {
    stopVoicePreview();
    refCustomPreviewReset.current?.();
    refCustomPreviewReset.current = null;
  }, [stopVoicePreview]);

  useEffect(() => {
    if (isCustomVoiceGenerating || isCustomVoicePlaying || !refCustomPreviewReset.current) {
      return;
    }

    refCustomPreviewReset.current();
    refCustomPreviewReset.current = null;
  }, [isCustomVoiceGenerating, isCustomVoicePlaying]);

  const playVoiceSample = useCallback(
    (shouldPlay: boolean, onAudioEnded: () => void, audioUrl?: string) => {
      if (!shouldPlay) {
        stopAllVoicePreview();
        return;
      }

      stopAllVoicePreview();
      if (!audioUrl) {
        onAudioEnded();
        return;
      }

      const previewAudio = new Audio(audioUrl);
      refVoicePreviewAudio.current = previewAudio;
      previewAudio.addEventListener("ended", onAudioEnded, { once: true });
      previewAudio.play().catch(() => {
        onAudioEnded();
      });
    },
    [stopAllVoicePreview],
  );

  const transformedPlatformVoices = useMemo<IChatVoice[]>(() => transformVoices(allVoices, playVoiceSample), [allVoices, playVoiceSample]);

  const transformedUserVoices = useMemo<IChatVoice[]>(
    () =>
      userVoices
        .filter((voice: VoiceWithCustomVoicePublicFields) => voice.status === VoiceStatus.READY_TO_USE)
        .map((voice: VoiceWithCustomVoicePublicFields) => ({
          id: voice.id,
          name: voice.name,
          label: voice.name,
          subLabel: voice.description,
          description: voice.description,
          audio_url: "",
          isCustom: true,
          onChange: (shouldPlay: boolean, reset: () => void) => {
            if (!shouldPlay) {
              refCustomPreviewReset.current?.();
              refCustomPreviewReset.current = null;
              stopCustomVoicePreview();
              return;
            }

            stopAllVoicePreview();
            refCustomPreviewReset.current = reset;
            const sampleText = getTranslatedVoiceSampleScript(voice.language);
            void previewVoice(sampleText || values.greeting || voice.description || voice.name || "", voice.id);
          },
        })),
    [previewVoice, stopAllVoicePreview, stopCustomVoicePreview, userVoices, values.greeting, getTranslatedVoiceSampleScript],
  );

  const allSelectableVoices = useMemo<IChatVoice[]>(
    () => (canUseCustomVoices ? [...transformedPlatformVoices, ...transformedUserVoices] : transformedPlatformVoices),
    [canUseCustomVoices, transformedPlatformVoices, transformedUserVoices],
  );

  const filteredVoices = useMemo(() => {
    switch (stateSelectedVoiceCategory) {
      case VoiceCategories.MALE:
        return transformedPlatformVoices.filter((voice) => voice.gender === Gender.Male);
      case VoiceCategories.FEMALE:
        return transformedPlatformVoices.filter((voice) => voice.gender === Gender.Female);
      case VoiceCategories.MY_VOICES:
        return canUseCustomVoices ? transformedUserVoices : [];
      default:
        return allSelectableVoices;
    }
  }, [allSelectableVoices, canUseCustomVoices, stateSelectedVoiceCategory, transformedPlatformVoices, transformedUserVoices]);

  const selectedVoice = useMemo(
    () => allSelectableVoices.find((voice) => voice.id === stateSelectedVoiceId),
    [allSelectableVoices, stateSelectedVoiceId],
  );

  const voiceCategoryList = useMemo(() => {
    const categories = [
      { name: VoiceCategories.ALL, label: t("common:all") },
      { name: VoiceCategories.MALE, label: t("common:male") },
      { name: VoiceCategories.FEMALE, label: t("common:female") },
    ];

    if (canUseCustomVoices) {
      categories.push({ name: VoiceCategories.MY_VOICES, label: t("voice:modal.attach.tab.myVoices") });
    }

    return categories;
  }, [canUseCustomVoices, t]);

  const fetchVoiceOptions = useCallback(async () => {
    const voiceRequests = canUseCustomVoices ? [getVoices(), getUserVoices()] : [getVoices()];
    await Promise.allSettled(voiceRequests);
  }, [canUseCustomVoices, getUserVoices, getVoices]);

  useEffect(() => {
    if (!initialValues.voice_id) {
      return;
    }

    void fetchVoiceOptions();
  }, [fetchVoiceOptions, initialValues.voice_id]);

  useEffect(() => {
    setStateSelectedVoiceId(voice_id);
  }, [voice_id]);

  useEffect(() => {
    return () => {
      stopAllVoicePreview();
    };
  }, [stopAllVoicePreview]);

  function extractDeepTag(input: DeepTag): string {
    while (typeof input !== "string" && input?.value !== undefined) {
      input = input.value;
    }
    return typeof input === "string" ? input : "";
  }

  function normalizeTagStrings(tags: DeepTag[]): string[] {
    return tags.map(extractDeepTag);
  }

  const buildPayload = useCallback((): IDraftChracter => {
    const payload = Object.fromEntries(
      Object.entries(values).filter(([, entryValue]) => entryValue !== null && entryValue !== undefined),
    ) as IDraftChracter;
    payload.tags = normalizeTagStrings(payload.tags ?? []);
    payload.group_addable = Boolean(values[CharacterFormFields.GROUP_ADDABLE]);
    if (stateSelectedVoiceId) {
      payload.voice_id = stateSelectedVoiceId;
    } else if (isEdit || initialValues.voice_id) {
      payload.voice_id = "";
    } else {
      delete payload.voice_id;
    }
    return payload;
  }, [initialValues.voice_id, isEdit, stateSelectedVoiceId, values]);

  const handleSave = useCallback(
    async (payload: IDraftChracter) => {
      // Allow the route transition triggered by a successful save/submit flow to bypass the unsaved-changes blocker.
      skipUnsavedChangesPromptRef.current = true;
      const { error } = (await onSave?.(payload, currentLorebooks, initialLorebooks, rulebookSelection.change)) || {};
      if (error) {
        skipUnsavedChangesPromptRef.current = false;
        return { error };
      }
      rulebookSelection.markSaved();
      setShowPrompt(false);
      setSuccessMessage(SUCCESS_MESSAGE);
      // removing draft if it exists
      const targetDraftId = draftId || persistedDraftIdRef.current || (isAlreadyDraft || isEdit ? id : undefined);
      if (targetDraftId) {
        const draftKey = getDraftStorageKey(userId);
        const drafts = JSON.parse(localStorage.getItem(draftKey) as string) || {};
        let hasRemovedDraft = false;
        if (drafts[targetDraftId]) {
          delete drafts[targetDraftId];
          hasRemovedDraft = true;
        } else if (isAlreadyDraft && id && drafts[id]) {
          delete drafts[id];
          hasRemovedDraft = true;
        }
        if (hasRemovedDraft) {
          localStorage.setItem(draftKey, JSON.stringify(drafts));
        }

        // The draft is gone, so the no-changes guard goes back to comparing against the server data.
        persistedDraftIdRef.current = null;
        setHasPersistedDraft(false);

        if (isAlreadyDraft) {
          navigate(MY_CHATBOT_PAGE_ROUTE);
        }
      }
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    },
    [onSave, currentLorebooks, initialLorebooks, rulebookSelection, draftId, isAlreadyDraft, isEdit, userId, id, navigate],
  );

  const proceedWithSave = useCallback(
    async (payload: IDraftChracter) => {
      const isCreatingPublicCharacter = payload.visibility === CHARACTER_VISIBILITY.PUBLIC && visibility !== payload.visibility;
      const wasCharacterModerated = moderationStatus === CHARACTER_MODERATION_STATUS.FIX_REQUIRED;
      const isPartnerApplication = getIsPartner();

      if (!isPartnerApplication && (isCreatingPublicCharacter || wasCharacterModerated || submitForManualReview)) {
        setPendingPayload(payload);
        return setShowConfirmationModal(true);
      }

      await handleSave(payload);
    },
    [visibility, moderationStatus, applicationId, submitForManualReview, handleSave],
  );

  const submitForm = useCallback(async () => {
    const payload = buildPayload();

    // `!isTokenCounterReady` guard: while the `gpt-token-utils` chunk is
    // still loading, the token counter stubs every field at 0 so the
    // validator cannot detect over-limit pre-populated content on edit
    // forms. Block submission until counts are trustworthy.
    if (!characterForm.isValid || tokenError || !isTokenCounterReady) {
      setShowRequiredFormFieldsError(true);

      const fieldOrder = [
        CharacterFormFields.NAME,
        CharacterFormFields.TITLE,
        CharacterFormFields.PERSONA,
        CharacterFormFields.GREETING,
        CharacterFormFields.TAGS,
        CharacterFormFields.GUIDELINES,
      ];

      if (isAvatarUploadAllowed) {
        fieldOrder.unshift(CharacterFormFields.AVATAR);
      }

      scrollToFirstInvalidField(findFirstInvalidFormizField({ fields: characterForm.fields, fieldOrder }));
      return;
    }

    const isGoingNonPublic =
      (payload.visibility === CHARACTER_VISIBILITY.PRIVATE || payload.visibility === CHARACTER_VISIBILITY.HIDDEN) &&
      visibility === CHARACTER_VISIBILITY.PUBLIC &&
      isEdit;

    if (isGoingNonPublic) {
      setPendingPayload(payload);
      setStateShowFavoriteWarningModal(true);
      return;
    }

    await proceedWithSave(payload);
  }, [buildPayload, characterForm, tokenError, isTokenCounterReady, visibility, isEdit, proceedWithSave]);

  useEffect(() => {
    if (errorCode) {
      displayToast({ message: CUSTOM_ERROR_MESSAGES[errorCode as CustomErrorMessage], type: NotificationType.error });
    }
  }, [displayToast, errorCode]);

  const onModalClose = () => {
    setShowConfirmationModal(false);
    setPendingPayload(null);
  };

  const handleFavoriteWarningConfirm = useCallback(async () => {
    setStateShowFavoriteWarningModal(false);
    const payload = pendingPayload ?? buildPayload();
    setPendingPayload(null);
    await proceedWithSave(payload);
  }, [pendingPayload, buildPayload, proceedWithSave]);

  const handleFavoriteWarningCancel = useCallback(() => {
    setStateShowFavoriteWarningModal(false);
    setPendingPayload(null);
  }, []);

  const onModalConfirm = async () => {
    const payload = pendingPayload ?? buildPayload();
    const result = await handleSave(payload);
    // Parent (e.g. ChatbotEdit) opened the public-bot moderation dialog; close this modal — it uses closeOnActions={false} and would stay open underneath.
    if (result?.error === CHATBOT_SAVE_DEFERRED_FOR_MODERATION_CONFIRM) {
      onModalClose();
    }
  };

  const { isChoiceKnown: isRulebookChoiceKnown, currentRulebookId, markDraftSaved: markRulebookDraftSaved } = rulebookSelection;
  const persistDraft = useCallback(() => {
    setShowPrompt(false);
    const payload = buildPayload();
    payload.tags = normalizeTagStrings(payload.tags ?? []);
    payload.lorebooks = currentLorebooks;
    payload.has_lorebooks = currentLorebooks.length > 0;
    const draftKey = getDraftStorageKey(userId);
    const dId = id || draftId || uuidv4();
    payload.id = dId;
    payload.creator_username = username ?? undefined;
    payload.creator_user_id = userId ?? undefined;

    if (!payload?.title) {
      payload.title = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
    } // ex. date : 21/02/2025

    const drafts = JSON.parse(localStorage.getItem(draftKey) as string) || {};

    // Check if the draftId already exists in drafts
    if (drafts[dId]) {
      // Update the existing draft with the new payload
      drafts[dId] = { ...drafts[dId], ...payload };
    } else {
      // Add the new draft to the drafts object
      drafts[dId] = payload;
    }

    // adding moderation report in draft if we have
    if (moderationReport) {
      drafts[dId].moderationReport = moderationReport;
    }

    if (isEdit) {
      drafts[dId].isEdit = true;
    }
    // Kept beside the payload (never sent to the character API): restored drafts reopen with the same Rulebook.
    // Until the choice is known (gated off, or the attachment still loading) the draft keeps whatever it had.
    if (isRulebookChoiceKnown) {
      drafts[dId].rulebook_id = currentRulebookId;
    }

    localStorage.setItem(draftKey, JSON.stringify(drafts));
    if (isRulebookChoiceKnown) {
      markRulebookDraftSaved();
    }
    // Only claim a draft exists once the write went through, so a failed serialisation cannot leave
    // the deletion path below looking for a draft that was never stored.
    persistedDraftIdRef.current = dId;
    setHasPersistedDraft(true);
    // The persisted lorebooks become the new baseline so they no longer count as unsaved changes
    setInitialLorebooks(currentLorebooks);
    hasInitializedLorebooks.current = true;
    return dId;
  }, [
    buildPayload,
    currentLorebooks,
    draftId,
    id,
    isEdit,
    moderationReport,
    currentRulebookId,
    isRulebookChoiceKnown,
    markRulebookDraftSaved,
    userId,
    username,
  ]);

  const saveAsDraft = useCallback(() => {
    const dId = persistDraft();
    displayToast({ message: t("chatbot:form.toast.chatbotSaved"), type: NotificationType.success });
    if (!isEdit) {
      setTimeout(() => {
        navigate(`${ROUTES.createChatbot}/${dId}`, { state: { preventScrollToTop: true } });
      }, 0);
    }
  }, [displayToast, isEdit, navigate, persistDraft, t]);

  const handleSaveButtonClick = useCallback(() => {
    if (waitingForApproval) {
      displayToast({ message: t("error:editUnderReviewItem"), type: NotificationType.info });
      return;
    }
    if (isSubmitting) {
      return;
    }
    if (isEdit && !isDetailsChangedOnEdit && !hasDraft) {
      displayToast({ message: t("error:noChangesDetected"), type: NotificationType.info });
      return;
    }
    characterForm.submit();
  }, [characterForm, displayToast, hasDraft, isDetailsChangedOnEdit, isEdit, isSubmitting, t, waitingForApproval]);

  const handleOpenVoiceSelection = useCallback(async () => {
    if (!hasVoiceAttachmentFeatureFlag) {
      return;
    }

    if (!isUserPremium || !hasAccessToTTS) {
      setStateShowVoiceUpgradeModal(true);
      return;
    }

    setStateSelectedVoiceCategory(VoiceCategories.ALL);
    setStateShowVoiceSelectionModal(true);
    await fetchVoiceOptions();
  }, [fetchVoiceOptions, hasAccessToTTS, hasVoiceAttachmentFeatureFlag, isUserPremium]);

  const handleSelectVoice = useCallback(
    (selectedVoiceId: IChatVoice["id"]) => {
      stopAllVoicePreview();
      setStateSelectedVoiceId(selectedVoiceId);
      setStateShowVoiceSelectionModal(false);
    },
    [stopAllVoicePreview],
  );

  const handleRemoveVoice = useCallback(() => {
    stopAllVoicePreview();
    setStateSelectedVoiceId(undefined);
  }, [stopAllVoicePreview]);

  const handleCreateVoice = useCallback(() => {
    if (!hasCustomVoicesAccess) {
      setStateShowVoiceUpgradeModal(true);
      return;
    }

    stopAllVoicePreview();

    if (id) {
      const returnTo = hasVoiceAttachmentFeatureFlag
        ? `${pathname}?openVoiceSelection=true&voiceCategory=${encodeURIComponent(VoiceCategories.MY_VOICES)}`
        : pathname;
      setTimeout(() => {
        navigate(`${ROUTES.voiceCreate}?returnTo=${encodeURIComponent(returnTo)}`);
      }, 0);
    } else {
      const savedDraftId = persistDraft();
      displayToast({ message: t("chatbot:form.toast.chatbotSaved"), type: NotificationType.success });
      const returnTo = hasVoiceAttachmentFeatureFlag
        ? `${ROUTES.createChatbot}/${savedDraftId}?openVoiceSelection=true&voiceCategory=${encodeURIComponent(VoiceCategories.MY_VOICES)}`
        : `${ROUTES.createChatbot}/${savedDraftId}`;
      setTimeout(() => {
        navigate(`${ROUTES.voiceCreate}?returnTo=${encodeURIComponent(returnTo)}`);
      }, 0);
    }
  }, [displayToast, hasCustomVoicesAccess, hasVoiceAttachmentFeatureFlag, id, navigate, pathname, persistDraft, stopAllVoicePreview, t]);

  const handleVoiceModalOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        stopAllVoicePreview();
      }
      setStateShowVoiceSelectionModal(isOpen);
    },
    [stopAllVoicePreview],
  );

  useEffect(() => {
    const shouldOpenVoiceSelection = searchParams.get("openVoiceSelection") === "true";
    if (!shouldOpenVoiceSelection) {
      return;
    }

    if (!hasVoiceAttachmentFeatureFlag) {
      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.delete("openVoiceSelection");
      updatedSearchParams.delete("voiceCategory");
      setSearchParams(updatedSearchParams, { replace: true });
      return;
    }

    const voiceCategory = searchParams.get("voiceCategory");
    const nextCategory = voiceCategory === VoiceCategories.MY_VOICES && canUseCustomVoices ? VoiceCategories.MY_VOICES : VoiceCategories.ALL;

    setStateSelectedVoiceCategory(nextCategory);
    setStateShowVoiceSelectionModal(true);
    void fetchVoiceOptions();

    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.delete("openVoiceSelection");
    updatedSearchParams.delete("voiceCategory");
    setSearchParams(updatedSearchParams, { replace: true });
  }, [canUseCustomVoices, fetchVoiceOptions, hasVoiceAttachmentFeatureFlag, searchParams, setSearchParams]);

  const getValidationSummary = () => {
    const errors: string[] = [];
    Object.keys(characterForm.fields).forEach((fieldName) => {
      const field = characterForm.fields[fieldName];
      if (field.error) {
        errors.push(field.error);
      }
    });

    return errors;
  };

  const fieldTokensLength = useCallback(
    (name: string) =>
      countTokens(
        replaceMessageTokens(values[name], {
          characterName: values.name,
        }),
      ),
    [values, countTokens],
  );
  const fieldTokensString = (name: CharacterFormFields) => {
    const length = fieldTokensLength(name);
    return t("chatbot:form.label.tokens", { count: length });
  };

  const getCharAndTokenLabel = (name: CharacterFormFields) => {
    const tokenString = fieldTokensString(name);
    const characterMaxLength = CHARACTER_FORM_VALIDATION_SCHEMA[name as keyof typeof CHARACTER_FORM_VALIDATION_SCHEMA]?.maxLength || 0;
    const characterLength = characterForm.fields[name]?.value?.length || 0;
    const characterCountLabel = t("chatbot:form.label.charactersCount", { count: characterLength, max: characterMaxLength });

    return `${characterCountLabel} | ${tokenString}`;
  };

  const totalTokens = useMemo(() => {
    return (
      fieldTokensLength(CharacterFormFields.GREETING) +
      fieldTokensLength(CharacterFormFields.PERSONA) +
      fieldTokensLength(CharacterFormFields.SCENARIO) +
      fieldTokensLength(CharacterFormFields.DIALOGUE)
    );
  }, [fieldTokensLength]);

  useEffect(() => {
    const { isValid, errorMessage, warningMessage } = isCharacterTokenCountValid(totalTokens, botVisibility as CHARACTER_VISIBILITY, "v2");
    setTokenError(!isValid);
    setTokenErrorMessage(isValid ? "" : errorMessage || "");
    setTokenWarningMessage(warningMessage ?? "");
  }, [totalTokens, botVisibility]);

  const handleBotVisibilityChange = (visibility: CHARACTER_VISIBILITY) => {
    const isBecomingPublic = visibility === CHARACTER_VISIBILITY.PUBLIC && botVisibility !== CHARACTER_VISIBILITY.PUBLIC;
    const isBecomingNonPublic = visibility !== CHARACTER_VISIBILITY.PUBLIC;

    setBotVisibility(visibility);
    setFieldsValues({
      definition_visible: visibility === CHARACTER_VISIBILITY.PUBLIC,
      ...(isBecomingPublic ? { [CharacterFormFields.GROUP_ADDABLE]: true } : {}),
      ...(isBecomingNonPublic ? { [CharacterFormFields.GROUP_ADDABLE]: false } : {}),
    });
  };

  // Check if advanced fields have validation errors with content
  const scenarioField = characterForm.fields[CharacterFormFields.SCENARIO];
  const dialogueField = characterForm.fields[CharacterFormFields.DIALOGUE];
  const hasAdvancedFieldErrors =
    (!scenarioField?.isValid && !!scenarioField?.value?.toString()?.trim()) ||
    (!dialogueField?.isValid && !!dialogueField?.value?.toString()?.trim());

  useEffect(() => {
    if (submitForManualReview) {
      setShowConfirmationModal(true);
    }
  }, [submitForManualReview]);

  useEffect(() => {
    const prevValues = prevValuesRef.current;
    const currentValues = characterForm.values;

    const hasChanged = prevValues !== JSON.stringify(currentValues);

    if (hasChanged && submitForManualReview) {
      setIsSubmitForManualReview(false);
    }

    prevValuesRef.current = JSON.stringify(currentValues);
  }, [characterForm.values]);

  // Auto-expand Advanced section when advanced fields have validation errors
  useEffect(() => {
    if (hasAdvancedFieldErrors) {
      setShouldExpandAdvanced(true);
    }
  }, [hasAdvancedFieldErrors]);

  const SuggestedFieldButton = ({ field, label }: SuggestedFieldButtonProps) => (
    <GenerateSuggestedFieldButton
      field={field}
      onButtonClick={() => handleGenerateSuggestField(field)}
      disabled={waitingForApproval || !isTitleAndNameFilled || (activeGeneratingField !== null && activeGeneratingField !== field)}
      isLoading={activeGeneratingField === field}
      label={label}
    />
  );

  return (
    <>
      <ReactRouterPrompt when={shouldBlockNavigation}>
        {({ isActive, onConfirm, onCancel }) =>
          isActive ? (
            <DiscardChangesModalV2
              heading={t("chatbot:modal.unsavedChanges.title")}
              description={t("chatbot:modal.unsavedChanges.message")}
              isVisible={true}
              onUserConfirmation={onConfirm}
              handleClose={onCancel}
            />
          ) : null
        }
      </ReactRouterPrompt>
      <Formiz connect={characterForm} initialValues={initialValues} onSubmit={submitForm}>
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="flex flex-col gap-6 pt-6">
            <FormField
              required
              dataTestId="CharacterFormAvatarUploadCapabilityGate"
              showError={showRequiredFormFieldsError}
              name={CharacterFormFields.AVATAR}
              label={t("chatbot:form.label.avatar")}
              fieldInfoLink={
                EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.create_chatbot_docs?.avatar
              }
              as="custom"
              description={t("chatbot:form.description.avatar")}
              data-field-name={CharacterFormFields.AVATAR}
              validations={[
                {
                  rule: isRequired(),
                  message: t("chatbot:form.validation.avatar"),
                },
              ]}
            >
              <AvatarCreate
                disabled={waitingForApproval}
                characterAvatar={values.avatar_url}
                setCharacterAvatar={(value: string) => {
                  characterForm.setFieldsValues({ avatar_url: value });
                }}
                form={characterForm}
                formType={FormTypes.CHARACTER}
                isUploadAllowed={isCharacterUploadAvatarAllowed}
              />
            </FormField>

            {isCharacterImportCardAllowed && allowImport ? (
              <div className="flex flex-row justify-between gap-2">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-1">
                    <Text variant={TextVariants.LABEL_LARGE_MEDIUM}>
                      <Trans i18nKey="chatbot:form.label.importCharacterCard" />
                    </Text>
                    <LocalizedLink target="_blank" to={importingCharactersDocURL}>
                      <Icon icon={IconVariants.INFO} size={IconSize.SMALL} className="text-gray-12" />
                    </LocalizedLink>
                  </div>
                  <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
                    <Trans i18nKey="chatbot:form.description.importCharacterCard" />
                  </Text>
                </div>
                <Button
                  iconStart={IconVariants.DOWNLOAD}
                  variant={ButtonVariant.OUTLINED}
                  onClick={onCharacterCardUpload}
                  disabled={waitingForApproval}
                >
                  <Trans i18nKey="chatbot:form.label.importButton" />
                </Button>
              </div>
            ) : null}

            <FormField
              showError={showRequiredFormFieldsError}
              name={CharacterFormFields.NAME}
              as="custom"
              data-field-name={CharacterFormFields.NAME}
              validations={[
                {
                  rule: (value: string) => isRequired()(value?.trim()),
                  message: t("chatbot:validation.field.required"),
                },
                {
                  rule: (value: string) => isMinLength(3)(value?.trim()),
                  message: t("chatbot:validation.field.minLength", { minLength: 3 }),
                },
                {
                  rule: (value: string) => isMaxLength(20)(value?.trim()),
                  message: t("chatbot:validation.name.maxLength", { maxLength: 20 }),
                },
                {
                  rule: (value: string) => !isStringContainingURL(value),
                  message: nameUrlErrorMessage,
                },
              ]}
            >
              <Input
                label={t("chatbot:form.label.name")}
                helperText={t("chatbot:form.description.name")}
                placeholder={formPlaceholders[CharacterFormFields.NAME]}
                maxLength={CHARACTER_FORM_VALIDATION_SCHEMA[CharacterFormFields.NAME].maxLength}
                aria-labelledby={t("chatbot:form.label.name")}
                disabled={waitingForApproval}
                required
                fieldInfoLink={
                  EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.create_chatbot_docs?.name
                }
                classNames={{ input: PLACEHOLDER_STYLING }}
              />
            </FormField>

            <FormField
              showError={showRequiredFormFieldsError}
              name={CharacterFormFields.TITLE}
              as="custom"
              data-field-name={CharacterFormFields.TITLE}
              validations={[
                {
                  rule: (value: string) => isRequired()(value?.trim()),
                  message: t("chatbot:validation.field.required"),
                },
                {
                  rule: (value: string) => isMinLength(3)(value?.trim()),
                  message: t("chatbot:validation.field.minLength", { minLength: 3 }),
                },
                {
                  rule: (value: string) => isMaxLength(100)(value?.trim()),
                  message: t("chatbot:validation.title.maxLength", { maxLength: 100 }),
                },
                {
                  rule: (value: string) => !isStringContainingURL(value),
                  message: titleUrlErrorMessage,
                },
              ]}
            >
              <TextArea
                label={t("chatbot:form.label.title")}
                helperText={t("chatbot:form.description.title")}
                placeholder={formPlaceholders[CharacterFormFields.TITLE]}
                maxLength={CHARACTER_FORM_VALIDATION_SCHEMA[CharacterFormFields.TITLE].maxLength}
                aria-labelledby={t("chatbot:form.label.title")}
                disabled={waitingForApproval}
                required
                rows={1}
                fieldInfoLink={
                  EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.create_chatbot_docs?.title
                }
                className={PLACEHOLDER_STYLING}
              />
            </FormField>

            <FormField
              showError={showRequiredFormFieldsError}
              name={CharacterFormFields.GREETING}
              as="custom"
              data-field-name={CharacterFormFields.GREETING}
              validations={[
                {
                  rule: (value: string) => isRequired()(value?.trim()),
                  message: t("chatbot:validation.field.required"),
                },
                {
                  rule: (value: string) => isMinLength(3)(value?.trim()),
                  message: t("chatbot:validation.field.minLength", { minLength: 3 }),
                },
                {
                  rule: (value: string) => isMaxLength(2000)(value?.trim()),
                  message: t("chatbot:validation.greeting.maxLength", { maxLength: 2000 }),
                },
                {
                  rule: (value: string) => !isStringContainingURL(value),
                  message: greetingUrlErrorMessage,
                },
              ]}
            >
              <TextArea
                label={t("chatbot:form.label.greeting")}
                labelRightElement={<SuggestedFieldButton field={CharacterFormFields.GREETING} />}
                helperText={t("chatbot:form.description.greeting")}
                maxLength={CHARACTER_FORM_VALIDATION_SCHEMA[CharacterFormFields.GREETING].maxLength}
                showMaxLengthLabel={false}
                rows={4}
                maxRows={20}
                aria-labelledby={t("chatbot:form.label.greeting")}
                countLabel={getCharAndTokenLabel(CharacterFormFields.GREETING)}
                placeholder={formPlaceholders[CharacterFormFields.GREETING]}
                required
                disabled={waitingForApproval}
                className={PLACEHOLDER_STYLING}
                fieldInfoLink={
                  EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.create_chatbot_docs?.greeting
                }
              />
            </FormField>

            <FormField
              showError={showRequiredFormFieldsError}
              name={CharacterFormFields.PERSONA}
              as="textarea"
              data-field-name={CharacterFormFields.PERSONA}
              validations={[
                {
                  rule: (value: string) => isRequired()(value?.trim()),
                  message: t("chatbot:validation.field.required"),
                },
                {
                  rule: (value: string) => isMinLength(3)(value?.trim()),
                  message: t("chatbot:validation.field.minLength", { minLength: 3 }),
                },
                {
                  rule: (value: string) => isMaxLength(5000)(value?.trim()),
                  message: t("chatbot:validation.persona.maxLength", { maxLength: 5000 }),
                },
                {
                  rule: (value: string) => !isStringContainingURL(value),
                  message: personaUrlErrorMessage,
                },
              ]}
            >
              <TextArea
                label={t("chatbot:form.label.persona")}
                labelRightElement={<SuggestedFieldButton field={CharacterFormFields.PERSONA} />}
                helperText={t("chatbot:form.description.persona")}
                maxLength={CHARACTER_FORM_VALIDATION_SCHEMA[CharacterFormFields.PERSONA].maxLength}
                showMaxLengthLabel={false}
                rows={4}
                maxRows={20}
                onChange={(value: string) => setPersonaValue(value)}
                aria-labelledby={CharacterFormFields.PERSONA}
                countLabel={getCharAndTokenLabel(CharacterFormFields.PERSONA)}
                placeholder={formPlaceholders[CharacterFormFields.PERSONA]}
                required
                disabled={waitingForApproval}
                className={PLACEHOLDER_STYLING}
                fieldInfoLink={
                  EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.create_chatbot_docs?.personality
                }
              />
            </FormField>

            <div className="flex flex-col gap-md">
              <VisibilityButtons
                name={CharacterFormFields.VISIBILITY}
                validations={[
                  {
                    rule: (value: string) => isRequired()(value?.trim()),
                    message: "Visibility Is Required",
                  },
                ]}
                buttons={[
                  {
                    value: CHARACTER_VISIBILITY.PUBLIC,
                    label: t("chatbot:form.visibility.button.public"),
                    helperText: t("chatbot:form.visibility.public"),
                  },
                  {
                    value: CHARACTER_VISIBILITY.PRIVATE,
                    label: t("chatbot:form.visibility.button.private"),
                    helperText: t("chatbot:form.visibility.private"),
                  },
                  {
                    value: CHARACTER_VISIBILITY.HIDDEN,
                    label: t("chatbot:form.visibility.button.hidden"),
                    helperText: t("chatbot:form.visibility.hidden"),
                  },
                ]}
                title={t("chatbot:form.label.visibility")}
                aria-labelledby={t("chatbot:form.label.visibility")}
                setBotVisibility={handleBotVisibilityChange}
                botVisibility={botVisibility}
                isDisabled={isFixRequired || waitingForApproval || isPermanentlyPrivate}
              />
              <div className="flex flex-row items-center justify-between">
                <div className={cn("flex flex-col", { "opacity-50": waitingForApproval || botVisibility !== CHARACTER_VISIBILITY.PUBLIC || view })}>
                  <Text variant={TextVariants.LABEL_LARGE_MEDIUM} className={"text-gray-12"}>
                    <Trans i18nKey="chatbot:form.label.definitionVisibility" />
                  </Text>
                  <Text variant={TextVariants.LABEL_SMALL_REGULAR} className={"text-gray-11"}>
                    <Trans i18nKey="chatbot:form.description.definitionVisibility" />
                  </Text>
                </div>
                <FormField name="definition_visible" as="custom">
                  <Switch
                    checked={!!characterForm?.values?.definition_visible}
                    onCheckedChange={(checked: boolean) => {
                      setFieldsValues({ definition_visible: checked });
                    }}
                    disabled={waitingForApproval || botVisibility !== CHARACTER_VISIBILITY.PUBLIC || view}
                    size={Size.LARGE}
                  />
                </FormField>
              </div>
            </div>
            <FormField
              required={isBotVisibilityPublic}
              showError={showRequiredFormFieldsError}
              label={t("chatbot:form.label.tags")}
              name={CharacterFormFields.TAGS}
              as="custom"
              data-field-name={CharacterFormFields.TAGS}
              validations={[
                {
                  rule: () => isSelectedTagsValid,
                  message: t("chatbot:validation.tags.minLength", { minLength: 2 }),
                  deps: [isSelectedTagsValid],
                },
              ]}
              description={t("chatbot:form.description.tags")}
              countLabel={`${values.tags?.length || 0}/${MAXIMUM_TAG_LIMIT}`}
              fieldInfoLink={
                EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.create_chatbot_docs?.tags
              }
            >
              <CharacterTags
                disabled={waitingForApproval}
                initialValues={characterForm?.values?.tags}
                characterFields={{
                  name: characterForm?.values?.name,
                  title: characterForm?.values?.title,
                  greeting: characterForm?.values?.greeting,
                  persona: characterForm?.values?.persona,
                  scenario: characterForm?.values?.scenario,
                  dialogue: characterForm?.values?.dialogue,
                }}
                setSelectedTags={(value: { label: string; value: string }[]) => {
                  characterForm.setFieldsValues({ tags: value });
                }}
              />
            </FormField>

            <CharacterFormAdvanced
              getCharAndTokenLabel={getCharAndTokenLabel}
              waitingForApproval={waitingForApproval}
              suggestedFieldButton={SuggestedFieldButton}
              characterId={id}
              lorebooks={currentLorebooks as Lorebook[]}
              rulebookSelection={rulebookSelection}
              voiceId={stateSelectedVoiceId}
              voiceName={selectedVoice?.name}
              voiceDescription={selectedVoice?.description}
              shouldExpandAdvanced={shouldExpandAdvanced}
              onCreateVoiceClick={handleCreateVoice}
              onAttachVoiceClick={handleOpenVoiceSelection}
              onRemoveVoiceClick={handleRemoveVoice}
              onLorebookAttached={(lorebook: Lorebook) => {
                setCurrentLorebooks(() => [lorebook]);
              }}
              onLorebookDetached={(lorebookId: string) => {
                setCurrentLorebooks((prev: Lorebook[]) => prev.filter((lorebook: Lorebook) => lorebook.id !== lorebookId));
              }}
              groupAddable={values[CharacterFormFields.GROUP_ADDABLE] !== false}
              onGroupAddableChange={(value: boolean) => {
                setFieldsValues({ [CharacterFormFields.GROUP_ADDABLE]: value });
              }}
              isBotVisibilityPublic={isBotVisibilityPublic}
            />

            <TokenCounter
              currentTokens={totalTokens}
              maxTokens={MAX_TOKEN_LIMIT}
              hasError={tokenError}
              hasWarning={!tokenError && !!tokenWarningMessage}
              errorMessage={tokenErrorMessage}
              warningMessage={tokenWarningMessage}
              helperText={t("chatbot:form.tokensMessage")}
              label={t("chatbot:form.tokens")}
              infoLinkSlot={
                <LocalizedLink
                  target="_blank"
                  to={`${EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.docs}/advanced/tokens`}
                  aria-label="token-limit-information"
                >
                  <Icon icon={IconVariants.INFO} size={IconSize.SMALL} className="cursor-pointer text-gray-12" />
                </LocalizedLink>
              }
            />
            {allowImport ? (
              <FormField
                name={CharacterFormFields.GUIDELINES}
                as="checkbox"
                showError={showRequiredFormFieldsError}
                data-field-name={CharacterFormFields.GUIDELINES}
                validations={[
                  {
                    rule: (value) => value === true,
                    message: t("chatbot:form.validation.guidelines"),
                  },
                ]}
              >
                <Checkbox
                  name="guidelines"
                  aria-labelledby="guidelines"
                  size={Size.MEDIUM}
                  label={
                    <Text variant={TextVariants.LABEL_MEDIUM_REGULAR}>
                      <Trans i18nKey="chatbot:form.guidelines.prefix" />{" "}
                      <LocalizedLink
                        className="text-blue-10"
                        to={EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.guidelines}
                        target="_blank"
                        aria-label="community-guidelines"
                      >
                        <Trans i18nKey="chatbot:form.guidelines.link" />
                      </LocalizedLink>
                    </Text>
                  }
                />
              </FormField>
            ) : null}
            {((!isValid && !isStepPristine) || !isSelectedTagsValid) && (
              <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-red-9">
                {error}
              </Text>
            )}
            {submitError ? (
              <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-red-9">
                {submitError}
              </Text>
            ) : null}

            {successMessage && !submitForManualReview ? (
              <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-green-9">
                {successMessage}
              </Text>
            ) : null}

            {/* Validation error summary */}
            {!isStepPristine && !isValid && Boolean(getValidationSummary().length) && (
              <div className="flex flex-col md:flex-row gap-2">
                {getValidationSummary().map((error, index) => (
                  <div key={index} className="w-full md:w-1/2">
                    <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-red-9">
                      {error}
                    </Text>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col md:flex-row gap-2 justify-end">
              {!isEdit || isFixRequired ? (
                <Button
                  onClick={saveAsDraft}
                  variant={ButtonVariant.OUTLINED}
                  size={ButtonSize.LARGE}
                  aria-labelledby="draft"
                  disabled={(!isDetailsChangedOnEdit && !hasDraft && isFixRequired) || waitingForApproval}
                >
                  {id ? (
                    <Trans i18nKey="chatbot:page.create.draftButtonLabel.update" />
                  ) : (
                    <Trans i18nKey="chatbot:page.create.draftButtonLabel.save" />
                  )}
                </Button>
              ) : null}
              {onSave ? (
                <Button
                  size={ButtonSize.LARGE}
                  type="button"
                  onClick={handleSaveButtonClick}
                  aria-labelledby="create-and-chat"
                  loading={isSubmitting}
                  loadingWithText={true}
                  aria-disabled={isSaveButtonVisuallyDisabled}
                  className={isSaveButtonVisuallyDisabled ? "opacity-50 cursor-not-allowed" : ""}
                >
                  {isEdit ? <Trans i18nKey="chatbot:page.edit.saveButtonLabel" /> : <Trans i18nKey="chatbot:page.create.createButtonLabel" />}
                </Button>
              ) : null}
            </div>
          </div>
        </form>
      </Formiz>

      <CapabilityGate capability={SubCapabilityKey.TtsCharacterAttach}>
        <ChatModalVoiceSelection
          dataTestId="CharacterFormTtsCharacterAttachCapabilityGate"
          open={stateShowVoiceSelectionModal}
          listItems={filteredVoices}
          loadingList={voiceLoading.isLoading || isUserVoicesLoading}
          onVoiceSelection={handleSelectVoice}
          onSearch={stopAllVoicePreview}
          stopAudio={stopAllVoicePreview}
          defaultSelectedVoice={stateSelectedVoiceId}
          voiceSelectionLoading={false}
          onOpenChange={handleVoiceModalOpenChange}
          voiceCategoryList={voiceCategoryList}
          selectedCategory={stateSelectedVoiceCategory}
          onPressCategory={setStateSelectedVoiceCategory}
        />
      </CapabilityGate>

      <UnlockCustomVoicesModal isOpen={stateShowVoiceUpgradeModal} onClose={setStateShowVoiceUpgradeModal} />

      <SubmitForReviewModal
        title={t("chatbot:modal.review.heading")}
        onUserConfirmation={onModalConfirm}
        confirmationMessage={confirmText}
        isVisible={showConfirmationModal}
        actionButtonText={submitForManualReview ? t("chatbot:modal.review.actionButtonManual") : t("chatbot:modal.review.actionButton")}
        discardButtonText={t("chatbot:modal.review.discardButton")}
        isLoading={isSubmitting}
        handleClose={onModalClose}
      />

      {!!hasBotUpdateModeration && (
        <ConfirmationModal
          titleClassNames="text-left pl-0"
          heading={t("chatbot:modal.moderation.submit.modalTitle")}
          handleClose={handleModerationSubmitConfirmClose}
          isVisible={stateShowModerationSubmitConfirm}
          isLoading={stateModerationConfirmLoading}
          onUserConfirmation={handleModerationSubmitConfirm}
          confirmationMessage={
            <span className="block w-full text-left">
              <Trans i18nKey="chatbot:modal.moderation.submit.modalDescription" />
            </span>
          }
          actionButtonText={t("chatbot:modal.review.actionButton")}
          discardButtonText={t("chatbot:modal.review.discardButton")}
        />
      )}

      <ConfirmationModal
        heading={t("chatbot:modal.favoriteWarning.heading")}
        handleClose={handleFavoriteWarningCancel}
        onUserConfirmation={handleFavoriteWarningConfirm}
        confirmationMessage={t("chatbot:modal.favoriteWarning.message")}
        isVisible={stateShowFavoriteWarningModal}
        actionButtonText={t("chatbot:modal.favoriteWarning.cta.confirm")}
        discardButtonText={t("common:cancel")}
        isLoading={isSubmitting}
        screen="CreateOrEditCharacter"
        titleClassNames="!pl-0"
      />
    </>
  );
}

export default CharacterForm;
